import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import boxLogo from '../../../Assets/box_logo.svg';
import { BoxItem, BoxPathItem, BoxSelection } from '../hooks/useFocusMode';

interface BoxPanelProps {
  isOpen: boolean;
  // × / backdrop / Escape / Cancel button — all discard the in-progress
  // pick and close without touching the committed selection.
  onCancel: () => void;
  // Submit button — commits the staged pick (parent closes the modal too).
  onSubmit: (selection: BoxSelection) => void;

  loading: boolean;
  error: string;
  items: BoxItem[];
  // Path under "Box" — e.g. [{id:'1',name:'JobsOhio'}, {id:'2',name:'Site Selection'}]
  breadcrumb: BoxPathItem[];
  committedSelection: BoxSelection;

  searchValue: string;
  onSearchChange: (value: string) => void;

  onNavigateInto: (item: BoxItem) => void;
  onNavigateToBreadcrumb: (index: number) => void; // -1 = "Box" root
}

// Staged pick, local to this modal until Submit — browsing folders never
// changes what's actually selected, only Submit does. path is the
// breadcrumb at the moment the item was picked (not at Submit — the user
// may browse elsewhere in between), so the picker can reopen where it lives.
type Staged =
  | { type: 'none' }
  | { type: 'all' }
  | { type: 'folder'; id: string; name: string; path: BoxPathItem[] }
  | { type: 'file'; id: string; name: string; path: BoxPathItem[] };

const toStaged = (sel: BoxSelection): Staged => {
  if (sel.type === 'all') return { type: 'all' };
  if (sel.type === 'folder' && sel.id) return { type: 'folder', id: sel.id, name: sel.name ?? 'Folder', path: sel.path ?? [] };
  if (sel.type === 'file' && sel.id) return { type: 'file', id: sel.id, name: sel.name ?? 'File', path: sel.path ?? [] };
  return { type: 'none' };
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

const formatModifiedDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const BoxPanel: React.FC<BoxPanelProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  error,
  items,
  breadcrumb,
  committedSelection,
  searchValue,
  onSearchChange,
  onNavigateInto,
  onNavigateToBreadcrumb,
}) => {
  const [staged, setStaged] = useState<Staged>(() => toStaged(committedSelection));

  // BoxPanel itself never unmounts (Modal just renders null while closed), so
  // re-seed the staged pick from whatever's actually committed every time the
  // modal opens — otherwise a discarded (Cancel'd) pick from a prior open
  // would still be sitting in local state the next time it's opened.
  useEffect(() => {
    if (isOpen) setStaged(toStaged(committedSelection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Brings the picked row into view whenever a list finishes loading —
  // mainly so reopening inside a long folder shows where the pick is.
  // Deliberately not re-run when the pick changes: the row clicked is
  // already on screen.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen || loading) return;
    listRef.current?.querySelector('.box-row.selected')?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, loading, items]);

  const filteredItems = items.filter((item) => {
    if (!searchValue.trim()) return true;
    return item.name.toLowerCase().includes(searchValue.trim().toLowerCase());
  });

  const handleSubmit = () => {
    if (staged.type === 'none') return;
    if (staged.type === 'all') {
      onSubmit({ type: 'all', id: null, name: null });
    } else {
      onSubmit({ type: staged.type, id: staged.id, name: staged.name, path: staged.path });
    }
  };

  const summaryText =
    staged.type === 'none' ? 'No selection yet' :
    staged.type === 'all'  ? 'Box: Use files available' :
    staged.type === 'folder' ? `1 folder selected · ${staged.name}` :
    `1 file selected · ${staged.name}`;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} ariaLabel="Select from Box" maxWidth={600}>
      <div className="box-modal">
        {/* ── Header ── */}
        <div className="box-modal-header">
          <img src={boxLogo} alt="" className="box-modal-header-icon" aria-hidden="true" />
          <div className="box-modal-header-text">
            <div className="box-modal-title">Select from Box</div>
            <div className="box-modal-subtitle">Choose a folder/file name to load into this chat, or use all of Box</div>
          </div>
          <button
            type="button"
            className="box-modal-close"
            onClick={onCancel}
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="box-modal-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search files and folders in Box"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* ── Box: Use files available ── */}
        <button
          type="button"
          className={`box-use-all ${staged.type === 'all' ? 'selected' : ''}`}
          onClick={() => setStaged({ type: 'all' })}
        >
          <span className={`box-radio ${staged.type === 'all' ? 'checked' : ''}`} aria-hidden="true" />
          <span className="box-use-all-text">
            <span className="box-use-all-title">Box: Use files available</span>
            <span className="box-use-all-desc">Prompt across every file you have access to — no specific folder/file required</span>
          </span>
        </button>

        <div className="box-modal-section-label">OR SELECT A SPECIFIC FILE</div>

        {/* ── Breadcrumb ── */}
        <div className="box-breadcrumb">
          <button type="button" className="box-breadcrumb-item" onClick={() => onNavigateToBreadcrumb(-1)}>
            Box
          </button>
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={crumb.id}>
              <span className="box-breadcrumb-sep" aria-hidden="true">›</span>
              <button type="button" className="box-breadcrumb-item" onClick={() => onNavigateToBreadcrumb(i)}>
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* ── List ── */}
        {error && <div className="focus-error">{error}</div>}
        <div className="box-modal-list" ref={listRef}>
          {loading && <div className="focus-loading"><i className="fas fa-spinner fa-spin" /> Loading…</div>}
          {!loading && filteredItems.length === 0 && (
            <div className="focus-empty">No items to show.</div>
          )}
          {!loading && filteredItems.map((item) => {
            const isFolder = item.type === 'folder';
            const isSelected =
              (staged.type === 'folder' && isFolder && staged.id === item.id) ||
              (staged.type === 'file' && !isFolder && staged.id === item.id);
            const modified = formatModifiedDate(item.modifiedAt);

            return (
              <div key={item.id} className={`box-row ${isSelected ? 'selected' : ''}`}>
                <button
                  type="button"
                  className="box-row-radio-btn"
                  aria-label={`Select ${item.name}`}
                  onClick={() => setStaged({ type: isFolder ? 'folder' : 'file', id: item.id, name: item.name, path: breadcrumb })}
                >
                  <span className={`box-radio ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="box-row-body"
                  onClick={() =>
                    isFolder
                      ? onNavigateInto(item)
                      : setStaged({ type: 'file', id: item.id, name: item.name, path: breadcrumb })
                  }
                >
                  <i className={`fas ${isFolder ? 'fa-folder' : 'fa-file'} box-row-icon`} aria-hidden="true" />
                  <span className="box-row-text">
                    <span className="box-row-name">{item.name}</span>
                    <span className="box-row-meta">
                      {modified ? `Modified ${modified}` : 'Modified date unknown'}
                      {item.sizeBytes != null ? ` · ${formatBytes(item.sizeBytes)}` : ''}
                    </span>
                  </span>
                </button>
                {isFolder && (
                  <button
                    type="button"
                    className="box-row-chevron"
                    aria-label={`Open ${item.name}`}
                    onClick={() => onNavigateInto(item)}
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div className="box-modal-footer">
          <span className="box-footer-summary">{summaryText}</span>
          <div className="box-footer-actions">
            <button type="button" className="box-btn-cancel" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="box-btn-submit"
              onClick={handleSubmit}
              disabled={staged.type === 'none'}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BoxPanel;
